import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { deepEqual } from 'fast-equals';
import queryString from 'query-string';

import { firePixelServer } from 'actions/pixelServer';
import { fetchMyAccountContent } from 'apis/zcs';
import { cancelOrderHistorySearch, fetchOrderHistorySearch, fetchOrders } from 'actions/account/orders';
import { checkCustomerAuthentication } from 'actions/authentication';
import { PageLoader } from 'components/Loader';
import BackNav from 'components/account/BackNav';
import AccountBreadcrumbs from 'components/account/AccountBreadcrumbs';
import AccountHeader from 'containers/account/AccountHeader';
import OrderHistoryOverview from 'components/account/OrderHistoryOverview/OrderHistoryOverview';
import { PAGE_TYPE_MAP } from 'helpers/analytics';
import { onEvent } from 'helpers/EventHelpers';
import { amethystWindowClickEventHandler } from 'helpers/MyAccountRenderUtils';
import { titaniteView, track } from 'apis/amethyst';
import { evOrderHistoryPageView } from 'events/account';
import { calculateEstimatedOrderTotal, isMaybeGuestAuthOrderFlow } from 'helpers/MyAccountUtils';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import withUseMyAccountRedesignHOC from 'helpers/useMyAccountRedesignHOCWrapper';
import { fetchAccountInfo } from 'actions/account/account';

import styles from 'styles/containers/account/orderHistory.scss';

export class OrderHistory extends Component {
  componentDidMount() {
    const {
      firePixelServer,
      checkCustomerAuthentication,
      fetchMyAccountContent,
      account: { content },
      isMyAccRedesignHookValue: isMyAccRedesign,
      location: { search }
    } = this.props;
    const returnTo = '/orders';
    // do not redirect if auth_token is present
    if (isMyAccRedesign && !isMaybeGuestAuthOrderFlow(search)) {
      this.context.router.pushPreserveAppRoot('/my-orders/');
    }

    checkCustomerAuthentication(returnTo, () => this.fetchData(), {
      redirectOnClose: '/'
    });

    firePixelServer('order-history');
    titaniteView();

    if (!content) {
      fetchMyAccountContent();
    }

    onEvent(window, 'click', this.orderHistoryAmethystWindowClickEventHandler, true, this); // the fourth parameter ensures this event happens before other click events
  }

  componentDidUpdate(prevProps) {
    const {
      location: { search: prevSearch },
      fetchOrders,
      firePixelServer,
      orders: { orders: prevOrders } = {}
    } = prevProps;
    const {
      killswitch: { hasExpandedOrderHistorySearch },
      location: { search: nextSearch },
      orders: { totalPages, orders } = {},
      fetchOrderHistorySearch
    } = this.props;
    const prevParams = queryString.parse(prevSearch);
    const nextParams = queryString.parse(nextSearch);

    if (!deepEqual(prevParams, nextParams)) {
      if (hasExpandedOrderHistorySearch && nextParams.search) {
        fetchOrderHistorySearch(nextParams, { returnTo: '/orders' });
      } else {
        fetchOrders(nextParams, { returnTo: '/orders' });
      }
      firePixelServer('order-history');
    }

    if (!deepEqual(prevOrders, orders) || !deepEqual(prevParams, nextParams)) {
      // only fire if all the data needed for event has loaded, also fires if orders have changed
      const convertedPageSize = parseInt(nextParams.pageSize);
      const estimatedTotalOrders = calculateEstimatedOrderTotal(convertedPageSize, totalPages, orders.length);
      track(() => [evOrderHistoryPageView, { numberOfOrders: orders.length, estimatedTotalOrders }]); // use orders if totalPages === 1;
    }
  }

  orderHistoryAmethystWindowClickEventHandler = event => {
    amethystWindowClickEventHandler(event, PAGE_TYPE_MAP.orders);
  };

  fetchData() {
    const {
      killswitch: { hasExpandedOrderHistorySearch },
      orders: { orders },
      location: { search },
      fetchOrders,
      account: { customerInfo },
      fetchOrderHistorySearch,
      fetchAccountInfo
    } = this.props;
    const queryParams = queryString.parse(search);
    const returnTo = '/orders';

    if (!customerInfo) {
      fetchAccountInfo(returnTo);
    }

    if (!orders) {
      if (hasExpandedOrderHistorySearch && queryParams.search) {
        fetchOrderHistorySearch(queryParams, { returnTo });
      } else {
        fetchOrders(queryParams, { returnTo });
      }
    }
  }

  render() {
    const {
      location,
      killswitch: { hasExpandedOrderHistorySearch },
      orders: { totalPages, orders, isLoading, searchOrdersError },
      fetchOrders,
      fetchOrderHistorySearch,
      cancelOrderHistorySearch,
      history
    } = this.props;

    if (!orders) {
      return <PageLoader centered={true} />;
    } else {
      return (
        <SiteAwareMetadata>
          <div className={styles.orderHistory}>
            <div className={styles.header}>
              <AccountBreadcrumbs currentText="Order History" />
              <BackNav to="/account">My Account</BackNav>
              <AccountHeader title="Order History" />
            </div>
            <OrderHistoryOverview
              location={location}
              history={history}
              orders={orders}
              searchOrdersError={searchOrdersError}
              totalPages={totalPages}
              isLoading={isLoading}
              onFetchOrders={fetchOrders}
              hasExpandedOrderHistorySearch={hasExpandedOrderHistorySearch}
              cancelOrderHistorySearch={cancelOrderHistorySearch}
              onFetchOrderHistorySearch={fetchOrderHistorySearch}
            />
          </div>
        </SiteAwareMetadata>
      );
    }
  }
}

OrderHistory.contextTypes = {
  testId: PropTypes.func,
  router: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { orders, account, killswitch } = state;

  return {
    orders,
    account,
    killswitch
  };
};

export default connect(mapStateToProps, {
  fetchOrderHistorySearch,
  cancelOrderHistorySearch,
  fetchOrders,
  fetchAccountInfo,
  fetchMyAccountContent,
  firePixelServer,
  checkCustomerAuthentication
})(withUseMyAccountRedesignHOC(OrderHistory, 'isMyAccRedesignHookValue'));
